import React, { useState, useRef, useEffect, useMemo } from "react";
import { Box, Grid } from "@mui/material";
import styled from "styled-components";
import Dropzone from "react-dropzone";
import { FILTER_NOTICE } from "src/scenes/Noticeboard/Noticeboard";
import ImageIcon from "src/img/notice/image.svg";
import VideoIcon from "src/img/notice/video.svg";
import DocumentIcon from "src/img/notice/document.svg";
import RemoveIcon from "src/img/notice/remove-icon.svg";
import moment from "moment";
import NoticeModalSocialPlatform from "./NoticeModalSocialPlatformItems";
import { CLIENT_NOTICEBOARD, NOTICEBOARD } from "src/constants/permissions";
import { formatLocalDateTime } from "src/utils/dates";
import {
    FormField,
    LottieLoadingIndicator,
    Toast, Checkbox, ValidatedDateInput,
    ImageCropModal, SegmentedButtonGroup,
    VideoCropModal,
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalErrorText,
    StandardModalFormGrid,
    StandardModalFormGroup,
    StandardModalHelperText,
    StandardModalLabel,
    StandardModalSection,
    StandardModalSectionHeader,
    StandardModalSectionTitle,
    StandardModalText,
    portalColors
} from "src/components";
import MediaFileInput from "./NoticeMediaPreview";
import { assetsFromExistingNotice, NoticeImage, NoticeVideo } from "../mediaAssets";
import RichTextEditor from "src/components/RichTextEditor/RichTextEditor";
import { captionToHtml, htmlToCaption } from "src/components/RichTextEditor/htmlToCaption";
import CarouselCropModal from "src/components/CarouselCrop";
import { NOTICE_IMAGE_ASPECT_RATIOS, NOTICE_VIDEO_ASPECT_RATIOS, POST_TYPE } from "../constants";
import pluralize from "pluralize";
import { capitalizeString, customNames } from "src/utils/helpers";
import { getExpiredSocialPageUuids, hasExpiredSocialAccounts } from "./AccountLinking/utils/socialAccountExpiry";
import { CheckInSection } from "./CheckInSection";
import { hasValidCheckIn, normaliseCheckInForRequest } from "../checkIn";

const StyledLabel = styled(StandardModalLabel)`
    display: block;
    font-size: ${p => p.size || "0.9375rem"};
`;

const NoticeModalContent = styled(Box)`
    display: grid;
    gap: 1rem;
`;

const StyledTitleInput = styled(FormField)`
    height: 46px;
    width: 100%;
    border: 1px solid ${portalColors.borderStrong};
    border-radius: 8px;
    text-align: left;
    font-family: Manrope, Helvetica, sans-serif;
    font-weight: 500;
    font-size: 0.9375rem;
    color: ${portalColors.text};
    box-shadow: ${portalColors.inputShadow};
    max-width: 100%;
    box-sizing: border-box;

    ::placeholder {
        text-align: left;
        color: ${portalColors.textSoft};
        font-family: Manrope, Helvetica, sans-serif;
        font-weight: 400;
    }
`;

const StyledDropzone = styled(Dropzone)`
    height: 12.938rem;
    border: 1px solid ${portalColors.borderStrong};
    border-radius: 8px;
    margin: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden; /* Ensure image doesn't overflow the container */
`;

const StyledAttachmentDropzone = styled(StyledDropzone)`
    align-items: normal;
    justify-content: flex-start;
    min-height: 8rem;
    height: 100%;
    width: 100%;
    border: none;
    background-color: ${p => p.$isDragging ? "rgba(130, 145, 178, 0.18)" : "transparent"};
`;

const ErrorMessage = styled(StandardModalErrorText)`
    &:empty {
        display: none;
    }
`;

export const Description = styled(StandardModalHelperText)`
    margin: ${p => p.margin || "0"};
`;

const CheckboxContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

const ScheduleGrid = styled(StandardModalFormGrid)`
    align-items: start;
`;

const MediaSecondaryGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    padding-top: 1rem;
`;

const NoticeModalFooter = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
    gap: 0.75rem;
    flex-wrap: wrap;
`;

const ExpiredSocialAccountsNotice = styled.div`
    display: flex;
    gap: 24px;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    @media (max-width: 768px) {
        align-items: flex-start;
        flex-direction: column;
        gap: 12px;
    }
`;

const ExpiredSocialAccountsMessage = styled.div`
    color: ${portalColors.danger};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.35;
`;

const LinkedAccountsButton = styled(StandardModalActionButton)`
    width: 11.25rem;
    min-width: 11.25rem;
`;

const SEND_TO = {
    EMPLOYEES: "employees",
    CLIENTS: "clients",
    EMPLOYEES_AND_CLIENTS: "employees_and_clients"
};

const ERROR_LIST = {
    SEND_TO: { "input": "visibleTo", "message": "*You must select 1 option." },
    SUBJECT: { "input": "subject", "message": "*Please enter your post subject here." },
    MESSAGE: { "input": "message", "message": "*Please enter your post message here." },
    SCHEDULE_DATE: { "input": "scheduleDate", "message": "*Please provide a post date or you can post it now" },
    CHECK_IN: { "input": "checkIn", "message": "*Select a category and location to finish this Check In, or remove it." },
};

export const MediaLimit = 9; // used to set up the total of media items we can attach here

const NoticeModal = ({
    user,
    showNoticeModal,
    isEditForm,
    handleCloseModal,
    noticeData,
    filterOption,
    createOrUpdateNotice,
    isFormLoading,
    requestDeleteNoticesConfirmation,
    responseMessage,
    emptyResponseMessage,
    retrieveLinkedAccounts,
    linkedAccounts,
    linkedAccountsFetched,
    hasSocialMediaPostingRole,
    canUseCheckedIn,
    openLinkedAccounts,
    lockToClientAudience,
}) => {
    const userHasClientManagement = lockToClientAudience || user.permissions.includes(CLIENT_NOTICEBOARD);
    const userHasNoticeboardPermission = !lockToClientAudience && user.permissions.includes(NOTICEBOARD);

    const DEFAULT_ADD_NEW_FORM_STATE = {
        visibleToEmployees: !lockToClientAudience && filterOption === FILTER_NOTICE.EMPLOYEE,
        visibleToClients: lockToClientAudience || filterOption === FILTER_NOTICE.CLIENT,
        subject: null,
        message: null,
        scheduleDate: "",
        isPostNow: false,
        isActive: false,
        allowComments: true,
        checkIn: null
    };

    const [formData, setFormData] = useState(noticeData === null ? DEFAULT_ADD_NEW_FORM_STATE : {
        visibleToEmployees: noticeData.publications.some(publication => publication.readerType === FILTER_NOTICE.EMPLOYEE),
        visibleToClients: noticeData.publications.some(publication => publication.readerType === FILTER_NOTICE.CLIENT),
        subject: noticeData.subject,
        message: noticeData.message,
        scheduleDate: noticeData.scheduleDate !== null ? noticeData.scheduleDate : "",
        isPostNow: noticeData.isActive,
        isActive: noticeData.isActive,
        allowComments: noticeData.allowComments,
        selectedSocialsToPostUuids: noticeData.postedTo.map(post => post.pageUuid),
        postedTo: noticeData.postedTo,
        richTextMessage: noticeData.richTextMessage,
        checkIn: canUseCheckedIn ? noticeData.checkIn || null : null
    });

    const [errors, setErrors] = useState([]);
    const [noticeMedia, setNoticeMedia] = useState(noticeData === null ? [] : assetsFromExistingNotice(noticeData));
    const dropzoneAttachmentRef = useRef();
    const [uploadedAttachmentFiles, setUploadedAttachmentFiles] = useState([]);
    const [deleteAttachmentFiles, setDeleteAttachmentFiles] = useState([]);
    const [showSocialsToPost, setShowSocialsToPost] = useState(false);
    const [base64ImageToCrop, setBase64ImageToCrop] = useState(null);
    const [base64VideoFileToCrop, setBase64VideoFileToCrop] = useState(null);
    const [mediaCropTrimData, setMediaCropTrimData] = useState({});
    const [croppingMediaIndex, setCroppingMediaIndex] = useState(null);
    const [carouselAssets, setCarouselAssets] = useState([]);
    const [carouselCurrentAsset, setCarouselCurrentAsset] = useState({});
    const [showAudienceRemovalConfirmation, setShowAudienceRemovalConfirmation] = useState(false);

    useEffect(() => {
        if (hasSocialMediaPostingRole) {
            retrieveLinkedAccounts();
        }
    }, [hasSocialMediaPostingRole]);

    const postType = useMemo(() => {
        if (noticeMedia.length === 0) {
            return POST_TYPE.TEXT;
        }
        if (noticeMedia.length > 1) {
            return POST_TYPE.CAROUSEL;
        }
        return noticeMedia[0].mediaType();
    }, [noticeMedia]);

    const lockedAspectRatio = useMemo(() => {
        const firstVideo = noticeMedia.find(item => item.mediaType() === POST_TYPE.VIDEO);
        if (firstVideo) {
            return firstVideo.aspectRatio;
        }
        const firstImage = noticeMedia.find(item => item.mediaType() === POST_TYPE.IMAGE);
        return firstImage ?
            firstImage.aspectRatio :
            undefined;
    }, [noticeMedia]);
    const hasVideoMedia = useMemo(() => {
        return noticeMedia.some(item => item.mediaType() === POST_TYPE.VIDEO);
    }, [noticeMedia]);
    const lockedAspectRatioSupportsVideoCrop = NOTICE_VIDEO_ASPECT_RATIOS.some(({ standardName }) => {
        return standardName === lockedAspectRatio;
    });
    const imageAspectRatios = hasVideoMedia && !lockedAspectRatioSupportsVideoCrop ?
        NOTICE_IMAGE_ASPECT_RATIOS :
        (hasVideoMedia ? NOTICE_VIDEO_ASPECT_RATIOS : NOTICE_IMAGE_ASPECT_RATIOS);

    useEffect(() => {
        const hasFacebook = linkedAccounts.Facebook && linkedAccounts.Facebook.length > 0;
        const hasInstagram = linkedAccounts.Instagram && linkedAccounts.Instagram.length > 0;
        const hasLinkedIn = linkedAccounts.LinkedIn && linkedAccounts.LinkedIn.length > 0;

        setShowSocialsToPost(
            (hasFacebook && checkPostedToPlatform(linkedAccounts.Facebook, formData.postedTo ?? [])) ||
            (hasInstagram && checkPostedToPlatform(linkedAccounts.Instagram, formData.postedTo ?? [])) ||
            (hasLinkedIn && checkPostedToPlatform(linkedAccounts.LinkedIn, formData.postedTo ?? []))
        );
    }, [linkedAccountsFetched, linkedAccounts]);

    const showExpiredSocialAccountsNotice = useMemo(() => {
        return hasExpiredSocialAccounts(linkedAccounts);
    }, [linkedAccounts]);

    const checkPostedToPlatform = (linkedAccounts, postedTo) => {
        if (!isEditForm) {
            return true;
        }
        const allPages = linkedAccounts.flatMap(account => account.pages);
        const allPagesUuidArray = allPages.flatMap(item => item.uuid);
        const allPostedToUuidArray = postedTo.flatMap(item => item.pageUuid);
        return allPagesUuidArray.some(value => allPostedToUuidArray.includes(value));
    };

    const handleInputChange = (e, key) => {
        // Remove input's error message
        removeErrorMessage(key);
        const updatedNoticeData = { ...formData, [key]: e.target.value };
        setFormData(updatedNoticeData);
    };

    const submitForm = () => {
        const submitFormData = {
            ...removeUnpostableSocialsFromSubmitData(formData),
            checkIn: canUseCheckedIn ? normaliseCheckInForRequest(formData.checkIn) : null
        };
        let requestBody = {};

        if (postType === POST_TYPE.CAROUSEL) {
            let carouselMedia = [];

            carouselMedia = noticeMedia.map((item, index) => {
                const isVideo = item.mediaType() === POST_TYPE.VIDEO;
                const isNewUpload = !item.urlSource;

                const baseMedia = {
                    mediaType: item.mediaType(),
                    aspectRatio: item.aspectRatio,
                };

                if (isNewUpload) {
                    const fileExtension = item.localDataBlob.type.split("/").pop();
                    baseMedia.newMediaExtension = fileExtension;
                } else {
                    baseMedia.existingMediaUuid = item.uuid;
                }

                if (isVideo) {
                    baseMedia.trimStart = item.trimStart ?? 0;
                    baseMedia.trimEnd = item.trimEnd ?? 0;
                    baseMedia.crop = {
                        x: item.cropping?.x ?? 0,
                        y: item.cropping?.y ?? 0,
                        width: item.cropping?.width ?? 0,
                        height: item.cropping?.height ?? 0,
                    };
                }

                return baseMedia;
            });
            requestBody = {
                ...submitFormData,
                carouselMedia,
                postType: POST_TYPE.CAROUSEL
            };
        } else {
            requestBody = {
                ...submitFormData,
                postType
            };
        }

        // Create/update notice
        switch (postType) {
            case POST_TYPE.IMAGE:
                createOrUpdateNotice(requestBody, noticeMedia, setErrors, uploadedAttachmentFiles, deleteAttachmentFiles);
                break;
            case POST_TYPE.VIDEO:
                createOrUpdateNotice(requestBody, noticeMedia, setErrors, uploadedAttachmentFiles, deleteAttachmentFiles);
                break;
            case POST_TYPE.TEXT:
                createOrUpdateNotice(requestBody, noticeMedia[0], setErrors, uploadedAttachmentFiles, deleteAttachmentFiles);
                break;
            case POST_TYPE.CAROUSEL:
                createOrUpdateNotice(requestBody, noticeMedia, setErrors, uploadedAttachmentFiles, deleteAttachmentFiles);
                break;
            default:
                Toast.error("Invalid media type.");
                break;
        }
    };

    const getRemovedReaderTypes = () => {
        if (noticeData === null) {
            return [];
        }

        const selectedReaderTypes = [];
        if (formData.visibleToEmployees) {
            selectedReaderTypes.push(FILTER_NOTICE.EMPLOYEE);
        }
        if (formData.visibleToClients) {
            selectedReaderTypes.push(FILTER_NOTICE.CLIENT);
        }

        return noticeData.publications
            .map(publication => publication.readerType)
            .filter(readerType => !selectedReaderTypes.includes(readerType));
    };

    const getReaderTypeLabel = readerType => {
        if (readerType === FILTER_NOTICE.EMPLOYEE) {
            return capitalizeString(pluralize(customNames.employees));
        }
        if (readerType === FILTER_NOTICE.CLIENT) {
            return capitalizeString(pluralize(customNames.clients));
        }
        return readerType;
    };

    const removedAudienceLabels = getRemovedReaderTypes().map(getReaderTypeLabel);
    const removedAudienceText = removedAudienceLabels.join(" & ");
    const removedAudienceDescription = removedAudienceLabels.length === 1 ?
        `${removedAudienceText} audience` :
        `${removedAudienceText} audiences`;

    const handleFormSubmit = () => {
        emptyResponseMessage();
        if (hasFormErrors()) {
            return;
        }

        if (getRemovedReaderTypes().length > 0) {
            setShowAudienceRemovalConfirmation(true);
            return;
        }

        submitForm();
    };

    const handleConfirmAudienceRemoval = () => {
        setShowAudienceRemovalConfirmation(false);
        submitForm();
    };

    const handleCloseAudienceRemovalConfirmation = () => {
        setShowAudienceRemovalConfirmation(false);
    };

    const removeUnpostableSocialsFromSubmitData = (data) => {
        const expiredSocialPageUuids = getExpiredSocialPageUuids(linkedAccounts);
        const removeExpiredSocialUuid = uuid => !expiredSocialPageUuids.includes(uuid);

        return {
            ...data,
            selectedSocialsToPostUuids: (data.selectedSocialsToPostUuids || []).filter(removeExpiredSocialUuid),
            socialsToRemove: (data.socialsToRemove || []).filter(removeExpiredSocialUuid),
        };
    };

    const dataURLtoFile = (dataUrl, filename) => {
        const arr = dataUrl.split(",");
        const mime = arr[0].match(/:(.*?);/)[1];
        const bStr = atob(arr[1]);
        let n = bStr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bStr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
    };

    const handleVisibleTo = type => {
        // Remove input's error message
        removeErrorMessage(ERROR_LIST.SEND_TO.input);

        if (type === SEND_TO.EMPLOYEES) {
            setFormData({ ...formData, visibleToEmployees: true, visibleToClients: false });
        } else if (type === SEND_TO.CLIENTS) {
            setFormData({ ...formData, visibleToEmployees: false, visibleToClients: true });
        } else if (type === SEND_TO.EMPLOYEES_AND_CLIENTS) {
            setFormData({ ...formData, visibleToEmployees: true, visibleToClients: true });
        }
    };

    const hasFormErrors = () => {
        let newErrors = [];

        if (!formData.visibleToEmployees && !formData.visibleToClients) {
            newErrors = [...newErrors, ERROR_LIST.SEND_TO];
        }
        if (!formData.subject) {
            newErrors = [...newErrors, ERROR_LIST.SUBJECT];
        }

        if (!formData.message) {
            newErrors = [...newErrors, ERROR_LIST.MESSAGE];
        }

        if (canUseCheckedIn && formData.checkIn && !hasValidCheckIn(formData.checkIn)) {
            newErrors = [...newErrors, ERROR_LIST.CHECK_IN];
        }

        if (!formData.isActive) { /** Noticeboard notification already sent, don't reschedule so no need to validate*/
            if (!formData.scheduleDate && formData.isPostNow === false) {
                newErrors = [...newErrors, ERROR_LIST.SCHEDULE_DATE];
            }
        }

        setErrors(newErrors);
        return newErrors.length > 0;
    };

    const removeErrorMessage = key => {
        setErrors(prevErrors => prevErrors.filter(error => error.input !== key));
    };

    const hasErrorType = type => {
        return errors.find(error => error.input === type.input);
    };

    const handleDrop = (acceptedFiles, mediaIndex) => {
        if (noticeMedia.length + (acceptedFiles?.length ?? 0) > MediaLimit) {
            return Toast.info("You cannot add more media files.");
        }
        const validImageTypes = ["image/jpeg", "image/png"];
        const validVideoTypes = ["video/mp4", "video/webm", "video/ogg"];
        if (acceptedFiles && acceptedFiles.length > 0) {
            const hasAcceptedVideo = acceptedFiles.some(file => validVideoTypes.includes(file.type));
            const videoCompatibleRatios = NOTICE_VIDEO_ASPECT_RATIOS.map(({ standardName }) => standardName);
            if (hasAcceptedVideo && lockedAspectRatio && !videoCompatibleRatios.includes(lockedAspectRatio)) {
                Toast.error("Videos can only be added to a carousel using a 16:9 or 1:1 aspect ratio.");
                return;
            }
            if (acceptedFiles.length > 1) {
                const readFile = (file, index) => {
                    return new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => {
                            if (validImageTypes.includes(file.type)) {
                                resolve({ index, type: "image", data: reader.result });
                            } else if (validVideoTypes.includes(file.type)) {
                                resolve({ index, type: "video", data: reader.result });
                            } else {
                                Toast.error(
                                    "Unsupported file type. Images may be JPEG or PNG format, and videos may be MP4, WebM, or OGG format."
                                );
                                resolve(null);
                            }
                        };
                        reader.onerror = reject;
                        reader.readAsDataURL(file);
                    });
                };
                // Wait for all FileReaders to complete
                Promise.all(acceptedFiles.map(readFile)).then((results) => {
                    const filtered = results.filter(Boolean);
                    setCarouselAssets(filtered);
                });
                return;
            }
            const file = acceptedFiles[0];

            const reader = new FileReader();
            reader.onload = () => {
                if (validImageTypes.includes(file.type)) {
                    setBase64ImageToCrop(reader.result);
                } else if (validVideoTypes.includes(file.type)) {
                    setCroppingMediaIndex(mediaIndex);
                    setBase64VideoFileToCrop(reader.result);
                } else {
                    Toast.error("Unsupported file type. Images may be JPEG or PNG format, and videos may be MP4, WebM, or OGG format.");
                    return;
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const onImageCropComplete = (file, width, height, aspectRatio) => {
        if (!file) {
            return;
        }

        const noticeMedia = new NoticeImage(
            null,
            file,
            width,
            height,
            undefined /* uuid */,
            aspectRatio
        );

        setNoticeMedia(prev => {
            return [...prev, noticeMedia];
        });

        setBase64ImageToCrop(null);
        setBase64VideoFileToCrop(null);
    };

    const onVideoCropComplete = (file, crop, trimStart, trimEnd, aspectRatio) => {
        if (!file) {
            return;
        }

        const cropping = {
            x: crop?.croppedAreaPixels?.x ?? 0,
            y: crop?.croppedAreaPixels?.y ?? 0,
            width: crop?.croppedAreaPixels?.width ?? 0,
            height: crop?.croppedAreaPixels?.height ?? 0
        };
        const videoMedia = new NoticeVideo(
            null,
            file,
            aspectRatio,
            undefined,
            cropping,
            trimStart,
            trimEnd
        );

        setMediaCropTrimData(prev => ({
            ...prev,
            [croppingMediaIndex]: {
                crop,
                trimStart: trimStart ?? 0,
                trimEnd: trimEnd ?? 0,
                aspectRatio
            }
        }));

        setBase64ImageToCrop(null);
        setBase64VideoFileToCrop(null);
        setFormData(prev => ({
            ...prev,
            trimStart: trimStart ?? 0,
            trimEnd: trimEnd ?? 0,
            aspectRatio: aspectRatio,
            crop: cropping,
        }));

        // TODO - Consider adjusting cropping/aspect of existing carousel media. Note this is tricky!
        // - Not all media have the same dimensions, so the crop rectangle won't always line up
        // - Some items already existed if we're updating a post now, and we should leave those alone

        setNoticeMedia(prev => {
            return [...prev, videoMedia];
        });
    };

    const handleDropAttachments = acceptedFiles => {
        if (noticeMedia.length + acceptedFiles.length > MediaLimit) {
            return Toast.info("You cannot add more media files.");
        }
        acceptedFiles.map(file => {
            // Check for existing filename with potential numbering
            const existingFile = uploadedAttachmentFiles.find(
                uploadedFile => uploadedFile.name === file.name
            );

            if (existingFile) {
                Toast.error("Similar filename found");
                return;
            }

            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                setUploadedAttachmentFiles(prevFiles => [
                    ...prevFiles, {
                        name: file.name,
                        type: file.type,
                        preview: dataURLtoFile(reader.result, file.name)
                    }
                ]);
            };
        });
    };

    const handleBrowseClickAttachment = (event) => {
        event.stopPropagation();
        if (dropzoneAttachmentRef.current) {
            dropzoneAttachmentRef.current.open();
        }
    };

    const handleRemoveAttachment = (mediaIndex) => {
        setNoticeMedia(prev => {
            const cloned = [...prev];
            const removed = cloned.splice(mediaIndex, 1);
            removed[0]?.free();
            return cloned;
        });
    };

    const handlePostNowChange = () => {
        const boolVal = !formData.isPostNow;

        if (boolVal) {
            setFormData({
                ...formData,
                isPostNow: boolVal,
                scheduleDate: ""
            });
        } else {
            setFormData({
                ...formData,
                isPostNow: boolVal
            });
        }
    };

    const handleAllowCommentsChange = () => {
        const boolVal = !formData.allowComments;

        if (boolVal) {
            setFormData({
                ...formData,
                allowComments: boolVal
            });
        } else {
            setFormData({
                ...formData,
                allowComments: boolVal
            });
        }
    };

    const onToggleSocialItem = (pageUuid) => {
        const currentSelected = formData.selectedSocialsToPostUuids || [];
        const currentSocialsToRemove = formData.socialsToRemove || [];

        if (isEditForm) {
            const socialsToRemove = currentSocialsToRemove.includes(pageUuid)
                ? currentSocialsToRemove.filter(id => id !== pageUuid) // Fixed variable here.
                : [...currentSocialsToRemove, pageUuid];

            const updatedFormData = {
                ...formData,
                socialsToRemove: socialsToRemove
            };
            setFormData(updatedFormData);
            return;
        }
        const updatedSelected = currentSelected.includes(pageUuid)
            ? currentSelected.filter(id => id !== pageUuid)
            : [...currentSelected, pageUuid];

        const updatedFormData = {
            ...formData,
            selectedSocialsToPostUuids: updatedSelected,
            socialsToRemove: currentSocialsToRemove
        };
        setFormData(updatedFormData);
    };

    const isNewRecordOrScheduledPost = noticeData === null || !noticeData.isActive;

    const datePostedFormat = (dateToUse) => {
        const localDate = moment.utc(dateToUse).local(); //let's use browser timezone
        return (
            <div>
                {moment(localDate).format("Do MMMM YYYY")}&nbsp;
                {moment(localDate).format("h:mm A")}
            </div>
        );
    };

    const isSendPostToEveryone = formData.visibleToEmployees && formData.visibleToClients;
    const visibleToValue = isSendPostToEveryone
        ? SEND_TO.EMPLOYEES_AND_CLIENTS
        : formData.visibleToEmployees
            ? SEND_TO.EMPLOYEES
            : formData.visibleToClients
                ? SEND_TO.CLIENTS
                : "";
    const visibleToOptions = [
        userHasNoticeboardPermission && {
            value: SEND_TO.EMPLOYEES,
            label: capitalizeString(pluralize(customNames.employees))
        },
        userHasClientManagement && {
            value: SEND_TO.CLIENTS,
            label: capitalizeString(pluralize(customNames.clients))
        },
        userHasNoticeboardPermission && userHasClientManagement && {
            value: SEND_TO.EMPLOYEES_AND_CLIENTS,
            label: `${capitalizeString(pluralize(customNames.employees))} & ${capitalizeString(pluralize(customNames.clients))}`
        }
    ].filter(Boolean);

    const setHtmlContent = htmlContent => {
        removeErrorMessage("message");
        setFormData({
            ...formData,
            richTextMessage: htmlContent,
            message: htmlToCaption(htmlContent)
        });
    };

    const handleCheckInChange = (checkIn) => {
        removeErrorMessage(ERROR_LIST.CHECK_IN.input);
        setFormData({
            ...formData,
            checkIn
        });
    };

    const messageContent = useMemo(() => {
        if (formData.richTextMessage) {
            return formData.richTextMessage;
        }
        return captionToHtml(formData.message || "");
    }, [formData]);

    return (
        <StandardModal
            open={showNoticeModal}
            title={noticeData !== null ? "Edit Post" : "New Post"}
            widthMode="wide"
            hideCloseButton
            footer={(
                <NoticeModalFooter>
                    <div>
                        {noticeData !== null && !isFormLoading && (
                            <StandardModalActionButton
                                type="button"
                                $variant="danger"
                                onClick={() => {
                                    handleCloseModal(false);
                                    requestDeleteNoticesConfirmation([noticeData.uuid || ""]);
                                }}
                            >
                                Delete Post
                            </StandardModalActionButton>
                        )}
                    </div>
                    <StandardModalActionGroup $pushRight>
                        {isFormLoading ? <LottieLoadingIndicator height="1.563rem" width="1.563rem" containerHeight="0" /> : (
                            <>
                                <StandardModalActionButton
                                    type="button"
                                    onClick={handleCloseModal}
                                >
                                    Cancel
                                </StandardModalActionButton>
                                <StandardModalActionButton
                                    type="button"
                                    $variant="primary"
                                    onClick={() => handleFormSubmit()}
                                >
                                    Save
                                </StandardModalActionButton>
                            </>
                        )}
                    </StandardModalActionGroup>
                </NoticeModalFooter>
            )}
        >
            <NoticeModalContent>

                <StandardModalSection>
                    <StandardModalFormGroup>
                        <StyledLabel>
                            Send Post To
                        </StyledLabel>
                        <SegmentedButtonGroup
                            options={visibleToOptions}
                            value={visibleToValue}
                            minWidth="14rem"
                            onChange={handleVisibleTo}
                        />
                        {hasErrorType(ERROR_LIST.SEND_TO) &&
                            <ErrorMessage>{ERROR_LIST.SEND_TO.message}</ErrorMessage>
                        }
                    </StandardModalFormGroup>
                </StandardModalSection>

                <StandardModalSection>
                    <StandardModalSectionHeader>
                        <div>
                            <StandardModalSectionTitle>
                                Media
                            </StandardModalSectionTitle>
                            <Description margin="0.35rem 0 0">
                                You can upload a single image or video, or choose multiple files to turn your post into a carousel.
                            </Description>
                        </div>
                    </StandardModalSectionHeader>
                    <Grid container direction="row" spacing={2}>
                        <Grid item sm={5}>
                            <MediaFileInput
                                key={noticeMedia[0]?.uuid}
                                previewWidth={noticeMedia[0]?.mediaPreviewSize()?.width ?? "auto"}
                                previewHeight={noticeMedia[0]?.mediaPreviewSize()?.height ?? "186.5px"}
                                mediaIndex={0}
                                postType={postType}
                                noticeMedia={noticeMedia[0]}
                                onDrop={(files) => handleDrop(files, 0)}
                                onRemove={() => handleRemoveAttachment(0)}
                                labelChange="Browse Files"
                                mediaPreviewSize={noticeMedia[0]?.mediaPreviewSize()}
                                trimStart={mediaCropTrimData[0]?.trimStart}
                                trimEnd={mediaCropTrimData[0]?.trimEnd}
                                crop={mediaCropTrimData[0]?.crop}
                            />
                        </Grid>
                        <Grid item sm={7}>
                            <MediaSecondaryGrid>
                                {
                                    Array.from({ length: (MediaLimit - 1) }, (_, i) => i + 1).map((index) => (
                                        <div key={index}>
                                            <MediaFileInput
                                                previewWidth={"99.34px"}
                                                previewHeight={"99.34px"}
                                                mediaIndex={index}
                                                postType={postType}
                                                noticeMedia={noticeMedia[index]}
                                                onDrop={(files) => handleDrop(files, index)}
                                                onRemove={() => handleRemoveAttachment(index)}
                                                topPosition="-10px"
                                                trimStart={mediaCropTrimData[index]?.trimStart}
                                                trimEnd={mediaCropTrimData[index]?.trimEnd}
                                                crop={mediaCropTrimData[index]?.crop}
                                            />
                                        </div>
                                    ))
                                }
                            </MediaSecondaryGrid>
                        </Grid>
                    </Grid>
                </StandardModalSection>

                <StandardModalSection>
                    <StandardModalFormGroup>
                        <StyledLabel>
                            Subject/Title
                        </StyledLabel>
                        <StyledTitleInput
                            value={formData.subject ?? ""}
                            placeholder="Enter your post subject here"
                            onChange={e => handleInputChange(e, "subject")}
                            border={hasErrorType(ERROR_LIST.SUBJECT) ? `1px solid ${portalColors.danger}` : `1px solid ${portalColors.borderStrong}`}
                            borderRadius="8px"
                            height="46px"
                            size="0.9375rem"
                            placeholderColor={portalColors.textSoft}
                            boxShadow={portalColors.inputShadow}
                        />
                        {hasErrorType(ERROR_LIST.SUBJECT) && <ErrorMessage>{ERROR_LIST.SUBJECT.message}</ErrorMessage>}
                        {responseMessage.SUBJECT_INVALID.message.length > 0 && <ErrorMessage>{responseMessage.SUBJECT_INVALID.message}</ErrorMessage>}
                    </StandardModalFormGroup>
                    <StandardModalFormGroup>
                        <StyledLabel>
                            Message
                        </StyledLabel>
                        <RichTextEditor
                            editorHeight="300px"
                            setContent={setHtmlContent}
                            error={hasErrorType(ERROR_LIST.MESSAGE)}
                            initialValue={messageContent}
                            placeholder="Enter your post message here"
                        />
                        {hasErrorType(ERROR_LIST.MESSAGE) && <ErrorMessage>{ERROR_LIST.MESSAGE.message}</ErrorMessage>}
                        {responseMessage.MESSAGE_INVALID.message.length > 0 && <ErrorMessage>{responseMessage.MESSAGE_INVALID.message}</ErrorMessage>}
                    </StandardModalFormGroup>
                </StandardModalSection>

                {canUseCheckedIn && (
                    <CheckInSection
                        token={user.token}
                        value={formData.checkIn}
                        onChange={handleCheckInChange}
                        error={hasErrorType(ERROR_LIST.CHECK_IN) ? ERROR_LIST.CHECK_IN.message : ""}
                    />
                )}

                <StandardModalSection>
                    <AttachmentDropzone
                        noticeData={noticeData}
                        dropzoneAttachmentRef={dropzoneAttachmentRef}
                        handleDropAttachments={handleDropAttachments}
                        handleBrowseClickAttachment={handleBrowseClickAttachment}
                        uploadedAttachmentFiles={uploadedAttachmentFiles}
                        setUploadedAttachmentFiles={setUploadedAttachmentFiles}
                        setDeleteAttachmentFiles={setDeleteAttachmentFiles}
                    />
                </StandardModalSection>

                <StandardModalSection>
                    <StandardModalFormGroup>
                        <StyledLabel>Allow Comments</StyledLabel>
                        <CheckboxContainer>
                            <Checkbox
                                color="#36be24"
                                height="32px"
                                width="32px"
                                padding="7px"
                                radius="10px"
                                value={true}
                                checked={formData.allowComments}
                                onChange={handleAllowCommentsChange}
                            />
                            <Description>
                                Check this box if you wish to allow all users to comment on this post.
                            </Description>
                        </CheckboxContainer>
                    </StandardModalFormGroup>
                </StandardModalSection>

                {!isNewRecordOrScheduledPost &&
                    <StandardModalSection>
                        <StyledLabel>Posted Date:</StyledLabel>
                        <StandardModalText color={portalColors.text}>
                            {datePostedFormat(noticeData ? noticeData.created_at : formData.scheduleDate)}
                        </StandardModalText>
                    </StandardModalSection>
                }

                {(hasSocialMediaPostingRole && showSocialsToPost) &&
                    <StandardModalSection>
                        <StandardModalSectionHeader>
                            <div>
                                <StandardModalSectionTitle>
                                    Post To Social Media
                                </StandardModalSectionTitle>
                                <Description margin="0.35rem 0 0">
                                    You can also select to share this post on your linked social media accounts.
                                </Description>
                            </div>
                        </StandardModalSectionHeader>

                        {showExpiredSocialAccountsNotice &&
                            <ExpiredSocialAccountsNotice>
                                <ExpiredSocialAccountsMessage>
                                    *One or more social accounts need reconnecting. Posts will still publish in Me Business,
                                    but won&apos;t post to social profiles until reconnected in the Me Business web portal.
                                </ExpiredSocialAccountsMessage>
                                <LinkedAccountsButton type="button" onClick={openLinkedAccounts}>
                                    Go to Linked Accounts
                                </LinkedAccountsButton>
                            </ExpiredSocialAccountsNotice>
                        }

                        <NoticeModalSocialPlatform
                            selectedSocialsUuids={formData.selectedSocialsToPostUuids}
                            linkedAccounts={linkedAccounts}
                            linkedAccountsFetched={linkedAccountsFetched}
                            onToggleSocialItem={onToggleSocialItem}
                            postedTo={formData.postedTo}
                            isEditForm={isEditForm}
                            scheduleDate={formData.scheduleDate}
                            postType={postType}
                        />
                    </StandardModalSection>
                }

                {isNewRecordOrScheduledPost &&
                    <StandardModalSection>
                        <StandardModalSectionHeader>
                            <StandardModalSectionTitle>
                                Schedule Post
                            </StandardModalSectionTitle>
                        </StandardModalSectionHeader>
                        <ScheduleGrid columns="minmax(0, 2fr) minmax(14rem, 1fr)" gap="1rem">
                            <StandardModalFormGroup>
                                <StyledLabel>Select Post Date</StyledLabel>
                                <Description>
                                    Your post will be sent to your selected users and posted on your business noticeboard on this date.
                                </Description>

                                <ValidatedDateInput
                                    placeholder="Select date and time"
                                    value={formData.scheduleDate ? moment(formData.scheduleDate, "YYYY-MM-DDTHH:mm", true).toDate() : null}
                                    onChange={(date) => {
                                        if (moment(date).isValid()) {
                                            const formatted = formatLocalDateTime(date, "datetime");
                                            handleInputChange({ target: { value: formatted } }, "scheduleDate");
                                        }
                                    }}
                                    errors={[]}
                                    disabled={formData.isPostNow}
                                    showErrors={false}
                                    minDate={new Date()}
                                    showTime={true}
                                    datePickerPlacement="top-start"
                                    timePickerPlacement="top"
                                    dateTimeColumns="repeat(2, minmax(13.25rem, 1fr))"
                                />
                                {hasErrorType(ERROR_LIST.SCHEDULE_DATE) && <ErrorMessage>{ERROR_LIST.SCHEDULE_DATE.message}</ErrorMessage>}
                                {responseMessage.SCHEDULE_INVALID.message.length > 0 && <ErrorMessage>{responseMessage.SCHEDULE_INVALID.message}</ErrorMessage>}
                            </StandardModalFormGroup>
                            <StandardModalFormGroup>
                                <StyledLabel>Post Now</StyledLabel>
                                <CheckboxContainer>
                                    <Checkbox
                                        color="#36be24"
                                        height="32px"
                                        width="32px"
                                        padding="7px"
                                        radius="10px"
                                        value={true}
                                        checked={formData.isPostNow}
                                        onChange={handlePostNowChange}
                                    />
                                    <Description>
                                        Check this box if you wish to post this now to your business noticeboard.
                                    </Description>
                                </CheckboxContainer>
                            </StandardModalFormGroup>
                        </ScheduleGrid>
                    </StandardModalSection>
                }
            </NoticeModalContent>
            {/* Carousel Cropper */}
            {carouselAssets.length > 0 && (
                <CarouselCropModal
                    onImageCropComplete={onImageCropComplete}
                    onVideoCropComplete={onVideoCropComplete}
                    carouselAssets={carouselAssets}
                    setCarouselAssets={setCarouselAssets}
                    carouselCurrentAsset={carouselCurrentAsset}
                    setCarouselCurrentAsset={setCarouselCurrentAsset}
                    noticeMedia={noticeMedia}
                    setNoticeMedia={setNoticeMedia}
                    onClose={() => setCarouselAssets([])}
                    lockedAspectRatio={lockedAspectRatio}
                />
            )}

            {/* Image Cropper */}
            {/* figure out how to only trigger this when attaching an image */}
            {base64ImageToCrop && (
                <ImageCropModal
                    imageSrc={base64ImageToCrop}
                    isOpen={true}
                    onClose={() => {
                        setBase64ImageToCrop(null);
                    }}
                    onCropComplete={(file, width, height, aspectRatioLabel) => onImageCropComplete(file, width, height, aspectRatioLabel)}
                    aspectRatios={imageAspectRatios}
                    allowedOrientations={["landscape", "portrait"]}
                    returnAspectRationResult={true}
                    lockedAspectRatio={lockedAspectRatio}
                />
            )}
            {base64VideoFileToCrop && (
                <VideoCropModal
                    videoSrc={base64VideoFileToCrop}
                    isOpen={true}
                    onClose={() => {
                        setBase64VideoFileToCrop(null);
                    }}
                    onCropComplete={(file, crop, trimStart, trimEnd, aspectRatioLabel) =>
                        onVideoCropComplete(file, crop, trimStart, trimEnd, aspectRatioLabel)
                    }
                    aspectRatios={NOTICE_VIDEO_ASPECT_RATIOS}
                    allowedOrientations={["landscape"]}
                    returnAspectRationResult={true}
                    lockedAspectRatio={lockedAspectRatio}
                />
            )}
            <StandardModal
                open={showAudienceRemovalConfirmation}
                title="Remove Noticeboard Audience?"
                widthMode="medium"
                hideCloseButton
                footer={(
                    <StandardModalActionGroup $pushRight>
                        <StandardModalActionButton
                            type="button"
                            onClick={handleCloseAudienceRemovalConfirmation}
                        >
                            Cancel
                        </StandardModalActionButton>
                        <StandardModalActionButton
                            type="button"
                            $variant="danger"
                            width="10rem"
                            minWidth="10rem"
                            onClick={handleConfirmAudienceRemoval}
                        >
                            Remove
                        </StandardModalActionButton>
                    </StandardModalActionGroup>
                )}
            >
                <StandardModalText color={portalColors.text}>
                    Removing this post from the {removedAudienceDescription} will permanently delete comments
                    and reactions for that audience. This cannot be undone. Do you want to continue?
                </StandardModalText>
            </StandardModal>
        </StandardModal>
    );
};

const AttachItemContainer = styled.div`
    box-shadow: ${portalColors.inputShadow};
    border: 1px solid ${portalColors.border};
    border-radius: 8px;
    width: min(345px, 100%);
    padding: 0.5rem;
    display: flex;
    align-items: center;
    box-sizing: border-box;

    & span.text {
        flex: 1;
        word-wrap: break-word;
        max-width: 75%;
        color: ${portalColors.text};
        font-family: Manrope, Helvetica, sans-serif;
        font-size: 0.875rem;
        font-weight: 600;
        line-height: 1.35;
    }
`;

const FileIconImg = styled.img`
    margin-right: 1rem;
    margin-left: 0.25rem;
    max-width: 25px;
`;

const RemoveImg = styled.img`
    margin-left: auto;  /* Push remove icon to the right */
    height: 37px;
    position: relative;
    cursor: pointer;
`;

const AttachedFilesList = styled.ul`
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    padding: 0;
    margin: 0;
    list-style-type: none;
`;

const AttachedFilesListItem = styled.li`
    width: min(345px, 100%);
`;

const AttachmentDropzone = (props) => {
    const { noticeData, dropzoneAttachmentRef, handleDropAttachments, handleBrowseClickAttachment, uploadedAttachmentFiles, setUploadedAttachmentFiles, setDeleteAttachmentFiles } = props;

    const [isDragging, setIsDragging] = useState(false);
    const handleDragEnter = () => setIsDragging(true);
    const handleDragLeave = () => setIsDragging(false);
    const handleDrop = (e) => {
        handleDropAttachments(e);
        setIsDragging(false);
    };

    function getFileType(fileName) {
        const extension = fileName.split(".").pop().toLowerCase();
        const mimeTypes = {
            "jpg": "image/jpeg",
            "jpeg": "image/jpeg",
            "png": "image/png",
            "gif": "image/gif",
            //"bmp": "image/bmp",

            // Video MIME types
            "mp4": "video/mp4",
            //"mov": "video/quicktime",
            "webm": "video/webm",
            "ogg": "video/ogg",
            //"avi": "video/x-msvideo",
        };
        return mimeTypes[extension] || "unknown";
    }

    const existingFiles = () => {
        if (noticeData === null || !noticeData.attachments)
            return [];

        return noticeData.attachments
            .map(attachment => ({
                uuid: attachment.uuid,
                name: attachment.filename,
                type: getFileType(attachment.filename)
            }));
    };

    return (
        <StyledAttachmentDropzone
            ref={dropzoneAttachmentRef}
            onDrop={handleDrop}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            disableClick={true}
            $isDragging={isDragging}
        >
            <StandardModalFormGroup>
                <StyledLabel>Attachments</StyledLabel>
                <AttachedFiles
                    setUploadedAttachmentFiles={setUploadedAttachmentFiles}
                    setDeleteAttachmentFiles={setDeleteAttachmentFiles}
                    existingFiles={existingFiles}
                    newFiles={uploadedAttachmentFiles}
                />
                <StandardModalActionButton
                    type="button"
                    width="11.75rem"
                    minWidth="11.75rem"
                    onClick={handleBrowseClickAttachment}
                >
                    Attach File to Post
                </StandardModalActionButton>
            </StandardModalFormGroup>
        </StyledAttachmentDropzone>
    );
};

const AttachedFiles = (props) => {
    const { setUploadedAttachmentFiles, setDeleteAttachmentFiles, existingFiles, newFiles } = props;

    const [modalExistingFiles, setModalExistingFiles] = useState(existingFiles());

    const fileIcon = (fileType) => {
        const validImageTypes = ["image/jpeg", "image/png", "image/gif"];
        const validVideoTypes = ["video/mp4", "video/webm", "video/ogg"]; // Add more as needed

        if (validImageTypes.includes(fileType)) {
            return ImageIcon;
        }

        if (validVideoTypes.includes(fileType)) {
            return VideoIcon;
        }

        return DocumentIcon;
    };

    const handleRemove = (fileNameToRemove, event) => {
        event.stopPropagation();
        setUploadedAttachmentFiles(prevFiles => prevFiles.filter((file) => file.name !== fileNameToRemove));
    };

    const handleRemoveExisting = (fileUuidToRemove, event) => {
        event.stopPropagation();
        // TODO: remove the attachment from the notice modal preview but not in the notice listing.
        setDeleteAttachmentFiles(prevFiles => [
            ...prevFiles, {
                attachmentUuid: fileUuidToRemove,
            }
        ]);

        setModalExistingFiles(prevFiles => prevFiles.filter((file) => file.uuid !== fileUuidToRemove));
    };

    return (
        <>
            {modalExistingFiles.length > 0 && (
                <AttachedFilesList>
                    {modalExistingFiles.map(existingFile => (
                        <AttachedFilesListItem key={"key_" + existingFile.uuid}>
                            <AttachItemContainer key={existingFile.name}>
                                <FileIconImg src={fileIcon(existingFile.type)} className="file-icon" alt="File Icon" />
                                <span className="text">{existingFile.name}</span>
                                <RemoveImg onClick={(e) => handleRemoveExisting(existingFile.uuid, e)} src={RemoveIcon} alt="Remove" className="remove-icon" />
                            </AttachItemContainer>
                        </AttachedFilesListItem>
                    ))}
                </AttachedFilesList>
            )
            }
            {newFiles.length > 0 && (
                <AttachedFilesList>
                    {newFiles.map(file => (
                        <AttachedFilesListItem key={"key_" + file.name}>
                            <AttachItemContainer key={file.name}>
                                <FileIconImg src={fileIcon(file.type)} className="file-icon" alt="File Icon" />
                                <span className="text">{file.name}</span>
                                <RemoveImg onClick={(e) => handleRemove(file.name, e)} src={RemoveIcon} alt="Remove" className="remove-icon" />
                            </AttachItemContainer>
                        </AttachedFilesListItem>
                    ))}
                </AttachedFilesList>
            )
            }
        </>
    );
};

export default NoticeModal;
